'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Purge ALL private client-side state for a given user (or all users if userId is null).
 * Called on logout and before loading a new user's data.
 */
function purgePrivateClientState(userId?: string | null) {
  if (typeof window === 'undefined') return;

  // Keys to always remove on logout
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Remove all Supabase session/auth tokens
      if (key.startsWith('sb-') || key.startsWith('supabase.')) {
        keysToRemove.push(key);
        continue;
      }

      // Remove locale preference (scoped or unscoped)
      if (key === 'cs_locale' || key.startsWith('cs_locale_')) {
        keysToRemove.push(key);
        continue;
      }

      // Remove any user-scoped cache keys
      if (userId && key.includes(userId)) {
        keysToRemove.push(key);
        continue;
      }

      // Remove generic private cache keys that should never persist across users
      const privateKeys = [
        'cs_signals', 'cs_waiting', 'cs_goals', 'cs_needs',
        'cs_matters', 'cs_preferences', 'cs_profile',
      ];
      if (privateKeys.some((pk) => key.startsWith(pk))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      try { localStorage.removeItem(key); } catch {}
    });
  } catch {}

  // Clear sessionStorage private keys
  try {
    const sessionKeysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') || key.startsWith('supabase.') || key.startsWith('cs_')) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach((key) => {
      try { sessionStorage.removeItem(key); } catch {}
    });
  } catch {}
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  // loading = true until the initial session check completes
  const [loading, setLoading] = useState(true);
  // Track the previous user UUID to detect account switching
  const prevUserIdRef = useRef<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    // Step 1: Resolve initial session from Supabase (authoritative)
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      const currentUser = initialSession?.user ?? null;
      setSession(initialSession);
      setUser(currentUser);
      prevUserIdRef.current = currentUser?.id ?? null;
      setLoading(false);
    });

    // Step 2: Listen for all subsequent auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      const newUser = newSession?.user ?? null;
      const newUserId = newUser?.id ?? null;
      const prevUserId = prevUserIdRef.current;

      // Detect user switch: a different UUID has authenticated
      if (prevUserId && newUserId && prevUserId !== newUserId) {
        // Purge the previous user's private state before loading the new user
        purgePrivateClientState(prevUserId);
      }

      // Detect logout: user went from authenticated to unauthenticated
      if (prevUserId && !newUserId) {
        purgePrivateClientState(prevUserId);
      }

      prevUserIdRef.current = newUserId;
      setSession(newSession);
      setUser(newUser);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out — purges all private client state before redirecting
  const signOut = async () => {
    const currentUserId = user?.id ?? null;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    // Purge private state immediately after signOut
    purgePrivateClientState(currentUserId);
    // Reset local state
    setUser(null);
    setSession(null);
    prevUserIdRef.current = null;
  };

  // Get Current User (re-fetches from Supabase — authoritative)
  const getCurrentUser = async () => {
    const {
      data: { user: freshUser },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return freshUser;
  };

  // OAuth Sign In
  const signInWithOAuth = async (provider: 'google' | 'apple' | 'azure') => {
    // Always derive the redirect origin from the current window location.
    // This ensures production (changedsince.com) and development (builtwithrocket.new)
    // each generate the correct callback URL without any hardcoded domain.
    const redirectOrigin = window.location.origin;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${redirectOrigin}/auth/callback`,
        queryParams:
          provider === 'google'
            ? { access_type: 'offline', prompt: 'consent' }
            : undefined,
      },
    });
    if (error) throw error;
    return data;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database (always scoped to authenticated user)
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    signInWithOAuth,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
